import React, { useEffect } from 'react';
import { gsap, TimelineMax } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { KmxContainer } from '../../../components/KmxContainer';

const BuyWithConfidence = () => {
    useEffect(() => {
        // Register GSAP ScrollTrigger plugin (typed)
        gsap.registerPlugin(ScrollTrigger);
        initScrollAnimations();
    }, []);

    const initScrollAnimations = () => {
        const tl = new TimelineMax();
        tl.to(
            '.buy-with-confidence--bordered-overlay',
            {
                'border-left-width': 0,
                'border-right-width': 0,
                'border-top-width': 0,
                'border-bottom-width': 0,
                ease: 'Power3.easeOut',
                duration: 3,
            },
            '-=3',
        );
        tl.from('.buy-with-confidence--darkened-overlay', { autoAlpha: 0, ease: 'Power3.easeOut', duration: 3 }, '-=3');
        tl.from(
            '.buy-with-confidence--content-container',
            { opacity: 0, y: '15.625rem', ease: 'Power3.easeOut', duration: 3 },
            '-=2',
        );

        ScrollTrigger.create({
            trigger: '.buy-with-confidence--background',
            start: 'top-=4rem top',
            end: `bottom-=8rem top`,
            pin: true,
            invalidateOnRefresh: true,
        });

        ScrollTrigger.create({
            trigger: '.buy-with-confidence--background',
            start: 'top-=15.25rem top',
            scrub: true,
            animation: tl,
        });
    };

    return (
        <BuyWithConfidenceBackground>
            <BuyWithConfidenceDarkenedOverlay />
            <BuyWithConfidenceBorderedOverlay />
            <BuyWithConfidenceMaxWidthContainer>
                <BuyWithConfidenceContent />
            </BuyWithConfidenceMaxWidthContainer>
        </BuyWithConfidenceBackground>
    );
};

export default BuyWithConfidence;

const BuyWithConfidenceDarkenedOverlay = () => (
    <Box
        className="buy-with-confidence--darkened-overlay"
        sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.32)',
            pointerEvents: 'none',
        }}
    />
);

const BuyWithConfidenceBorderedOverlay = () => (
    <Box
        className="buy-with-confidence--bordered-overlay"
        sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'transparent',
            borderStyle: 'inset',
            border: (theme) => `2rem solid ${theme.palette.background.default}`,
            pointerEvents: 'none',
            borderWidth: { md: '5rem', lg: '8rem' },
        }}
    />
);

const BuyWithConfidenceMaxWidthContainer = ({ children }: { children?: React.ReactNode }) => (
    <KmxContainer maxWidth="xl" className="buy-with-confidence--max-width-container">
        {children}
    </KmxContainer>
);

const BuyWithConfidenceContent = () => (
    <Stack
        className="buy-with-confidence--content-container"
        sx={{
            position: 'relative',
            maxWidth: '22.5rem',
        }}
        spacing={2}
    >
        <Typography
            component="h2"
            sx={{ typography: { xs: 'h4', sm: 'h3' } }}
            color="white"
            className="buy-with-confidence--heading"
        >
            Buy with <br />
            confidence
        </Typography>
        <Typography sx={{ typography: { sm: 'bodyLarge' } }} className="buy-with-confidence--copy" color="white">
            10-day money back guarantee and CarMax Certified quality
        </Typography>
    </Stack>
);

const BuyWithConfidenceBackground = ({ children }: { children?: React.ReactNode }) => (
    <Box
        className="buy-with-confidence--background"
        sx={{
            height: 'calc(100vh - 4rem)',
            width: '100%',
            backgroundColor: '#333333',
            backgroundImage: {
                xs: 'url(/images/buying-online/guarantee/buyonline-30days-sm.jpg)',
                lg: 'url(/images/buying-online/guarantee/buyonline-30days-xl.jpg)',
            },
            backgroundPosition: 'center right 48%',
            backgroundSize: 'cover',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-end', md: 'center' },
            py: '7.5rem',
        }}
    >
        {children}
    </Box>
);
